import type { ComponentPropsWithRef } from 'react'
import { useId } from 'react'

/**
 * **`ComponentPropsWithRef` et non `InputHTMLAttributes`** — [DECISIONS §338].
 *
 * Le champ de coupon reformate ce qu'on tape et doit REPOSER le curseur là où
 * il était (`grouperLaSaisie`) : sans accès à l'élément, la main repart à la fin
 * du champ à chaque frappe. React 19 passe `ref` comme une propriété ordinaire
 * — elle traverse donc `...rest` — mais `InputHTMLAttributes` ne la déclare pas,
 * et TypeScript refusait de la laisser passer.
 */
interface Props extends ComponentPropsWithRef<'input'> {
  label: string
  error?: string | undefined
  hint?: string | undefined
}

/**
 * Champ de saisie — 06 §11.
 *
 * L'erreur est liée par `aria-describedby` et annoncée : un message d'erreur
 * qu'un lecteur d'écran ne lit pas n'existe pas. RG-D18 : il dit ce qui s'est
 * passé ET quoi faire.
 */
export function Field({ label, error, hint, className = '', ...rest }: Props) {
  const id = useId()
  const errorId = `${id}-error`
  const hintId = `${id}-hint`

  const described = [error !== undefined ? errorId : null, hint !== undefined ? hintId : null]
    .filter(Boolean)
    .join(' ')

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-[13px] font-bold" style={{ color: 'var(--texte-2)' }}>
        {label}
      </label>

      <input
        id={id}
        {...rest}
        aria-invalid={error !== undefined}
        aria-describedby={described === '' ? undefined : described}
        className={`h-12 rounded-lg border px-3 text-[15px] outline-none focus:border-[var(--accent)] ${className}`}
        style={{
          background: 'var(--surface-2)',
          // RG-D1 — la bordure d'un CHAMP délimite un contrôle : 3:1, et non
          // le filet discret qui sépare deux items du fil.
          borderColor: error !== undefined ? 'var(--faux)' : 'var(--bordure-champ)',
          color: 'var(--texte-1)',
        }}
      />

      {hint !== undefined && (
        <p id={hintId} className="text-[13px]" style={{ color: 'var(--texte-2)' }}>
          {hint}
        </p>
      )}

      {error !== undefined && (
        <p id={errorId} role="alert" className="text-[13px]" style={{ color: 'var(--faux)' }}>
          {error}
        </p>
      )}
    </div>
  )
}
